
import {
  Activity,
  BarChart3,
  Calendar,
  CheckSquare,
  ClipboardList,
  Clock,
  DollarSign,
  FileText,
  FolderOpen,
  GitBranch,
  LayoutDashboard,
  Lightbulb,
  Link2,
  LogOut,
  Megaphone,
  MessageSquare,
  PenTool,
  Settings,
  Sparkles,
  Target,
  TrendingUp,
  User,
  UserCog,
  Users,
} from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarHeader, SidebarFooter, useSidebar } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import { useTheme } from "@/components/theme-provider";
import { useAuth } from "@/hooks/useAuth";
import { PlanUsageNavItem } from "@/components/usage/PlanUsageNavItem";
import { useWorkspace } from "@/hooks/useWorkspace";
import { usePermissions } from "@/hooks/usePermissions";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useProfile } from "@/hooks/useProfile";
import { BrandLogo } from "@/components/brand/BrandLogo";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";

const mainNavItems = [{
  title: "Dashboard",
  url: "/dashboard",
  icon: LayoutDashboard,
  onboardingId: "dashboard"
}, {
  title: "Minhas Tarefas",
  url: "/minhas-tarefas",
  icon: ClipboardList
}, {
  title: "Timesheet",
  url: "/timesheet",
  icon: Clock,
  permission: "admin"
}, {
  title: "Demandas",
  url: "/demandas",
  icon: FileText,
  onboardingId: "demands"
}];

const contentNavItems = [{
  title: "Calendário",
  url: "/calendario",
  icon: Calendar,
  onboardingId: "calendar"
}, {
  title: "Conteúdo",
  url: "/conteudo",
  icon: PenTool,
  onboardingId: "content"
}, {
  title: "Banco de Ideias",
  url: "/ideias",
  icon: Lightbulb,
  onboardingId: "ideas"
}];

const marketingNavItems = [{
  title: "Tráfego Pago",
  url: "/trafego",
  icon: TrendingUp
}, {
  title: "Campanhas",
  url: "/campanhas",
  icon: Megaphone
}, {
  title: "Métricas",
  url: "/relatorios",
  icon: BarChart3
}];

const managementNavItems = [{
  title: "Clientes",
  url: "/clientes",
  icon: Users,
  onboardingId: "clients"
}, {
  title: "Equipe",
  url: "/configuracoes?tab=team",
  icon: UserCog,
  onboardingId: "team",
  protected: true
}, {
  title: "Funil de Vendas",
  url: "/crm",
  icon: Target,
  permission: "admin"
}, {
  title: "Régua de Aderência",
  url: "/adherencia",
  icon: Activity,
  permission: "admin"
}, {
  title: "Financeiro",
  url: "/financeiro",
  icon: DollarSign,
  permission: "admin"
}];

const toolsNavItems = [{
  title: "Documentos",
  url: "/documentos",
  icon: FileText,
  protected: true
}, {
  title: "Mapa Mental",
  url: "/mapas-mentais",
  icon: GitBranch,
  protected: true
}, {
  title: "Formulários",
  url: "/formularios",
  icon: ClipboardList,
  protected: true
}, {
  title: "IA Assistente",
  url: "/ia",
  icon: Sparkles,
  onboardingId: "ai",
  permission: "ia"
}, {
  title: "Comunicação",
  url: "/comunicacao",
  icon: MessageSquare
}, {
  title: "Arquivos",
  url: "/arquivos",
  icon: FolderOpen
}];

const systemNavItems = [{
  title: "Integrações",
  url: "/integracoes",
  icon: Link2,
  permission: "integracoes"
}, {
  title: "Configurações",
  url: "/configuracoes",
  icon: Settings
}];

const clientNavItems = [{
  title: "Visão geral",
  url: "/dashboard",
  icon: LayoutDashboard,
}, {
  title: "Calendário",
  url: "/calendario",
  icon: Calendar,
}, {
  title: "Conteúdos",
  url: "/conteudo",
  icon: PenTool,
}, {
  title: "Relatórios",
  url: "/relatorios",
  icon: BarChart3,
}, {
  title: "Aprovações",
  url: "/aprovacoes",
  icon: CheckSquare,
}];

export function AppSidebar() {
  const { state } = useSidebar();
  const { density } = useTheme();
  const { user, signOut } = useAuth();
  const { profile } = useProfile();
  const { currentWorkspace } = useWorkspace();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const isClientWorkspace = currentWorkspace?.role === "client";
  
  const collapsed = state === "collapsed";
  const location = useLocation();
  const menuPathsWithSearch = [
    ...mainNavItems,
    ...contentNavItems,
    ...marketingNavItems,
    ...managementNavItems,
    ...toolsNavItems,
    ...systemNavItems,
    ...clientNavItems,
  ]
    .map((item) => item.url)
    .filter((url) => url.includes("?"));

  const isActive = (path: string) => {
    if (path.includes("?")) {
      return `${location.pathname}${location.search}` === path;
    }

    const currentPathWithSearch = `${location.pathname}${location.search}`;

    if (menuPathsWithSearch.includes(currentPathWithSearch)) {
      return false;
    }

    return location.pathname === path;
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  const displayName = profile?.full_name || user?.user_metadata?.full_name || "Usuário";
  const displayEmail = user?.email;

  const userInitials = displayName
    .split(" ")
    .map((n: string) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  // Density styles
  const itemPadding = density === 'compact' ? 'py-2 px-3' : 'py-3 px-3';
  const groupGap = density === 'compact' ? 'my-2' : 'my-4';
  const iconSize = density === 'compact' ? 'h-4 w-4' : 'h-5 w-5';
  const fontSize = density === 'compact' ? 'text-sm' : 'text-sm';
  const collapsedItemsGapClass = "!gap-2";
  const expandedItemsGapClass = "space-y-1";
  const collapsedGroupSpacingClass = "mt-5";
  const sidebarScrollbarClass = "scrollbar-sidebar-thin";
  const sidebarMenuButtonCollapsedClass =
    "group-data-[collapsible=icon]:!size-10 group-data-[collapsible=icon]:!rounded-[10px] group-data-[collapsible=icon]:!p-0 group-data-[collapsible=icon]:!aspect-square";
  const collapsedMenuStyle: React.CSSProperties = { rowGap: 8 };
  const collapsedMenuBtnStyle: React.CSSProperties = { width: 40, height: 40, padding: 0, borderRadius: 10 };

  type SectionKey = "principal" | "conteudo" | "marketing" | "gestao" | "ferramentas" | "sistema" | "cliente";

  const getSectionVar = (key: SectionKey) =>
    `hsl(var(--sidebar-section-${key}))`;

  const renderSectionLabel = (label: string, sectionKey: SectionKey) => (
    <SidebarGroupLabel
      className="mb-2 px-2 text-[10px] font-bold uppercase tracking-[0.24em]"
      style={{ color: getSectionVar(sectionKey) }}
    >
      {label}
    </SidebarGroupLabel>
  );

  const isNavItemVisible = (item: {
    protected?: boolean;
    permission?: string;
  }) => {
    if (item.protected && currentWorkspace?.role !== "owner" && currentWorkspace?.role !== "admin") {
      return false;
    }

    if (item.permission && !hasPermission(item.permission)) {
      return false;
    }

    return true;
  };

  const visibleManagementNavItems = managementNavItems.filter(isNavItemVisible);
  const visibleSystemNavItems = systemNavItems.filter(isNavItemVisible);

  const renderNavItem = (
    item: {
      title: string;
      url: string;
      icon: React.ComponentType<{ className?: string }>;
      onboardingId?: string;
      protected?: boolean;
      permission?: string;
    },
    sectionKey: SectionKey
  ) => {
    if (!isNavItemVisible(item)) return null;

    const active = isActive(item.url);
    const sectionColor = getSectionVar(sectionKey);
    const sectionBgActive = `hsl(var(--sidebar-section-${sectionKey}) / 0.18)`;
    const sectionShadowActive = `0 8px 24px hsl(var(--sidebar-section-${sectionKey}) / 0.22)`;

    return (
    <SidebarMenuItem key={item.title}>
      <SidebarMenuButton
        asChild
        tooltip={collapsed ? item.title : undefined}
        className={cn(sidebarMenuButtonCollapsedClass, collapsed ? "!h-10 !w-10 !aspect-square !p-0 !rounded-[10px]" : undefined, "transition-colors duration-150")}
        style={collapsed ? collapsedMenuBtnStyle : undefined}
      >
        <NavLink
          to={item.url}
          {...(item.onboardingId && { "data-onboarding": item.onboardingId })}
          data-section={sectionKey}
          data-active={active ? "true" : "false"}
          className={cn(
            "group relative flex items-center gap-3 border border-transparent transition-colors duration-150",
            collapsed ? "h-full w-full justify-center rounded-[10px]" : itemPadding + " rounded-xl",
            "text-white/88",
            active ? "shadow-[0_8px_24px_rgba(0,0,0,0.22)] text-white" : ""
          )}
          style={{
            ...(active && {
              backgroundColor: sectionBgActive,
              borderColor: sectionColor + "40",
              boxShadow: sectionShadowActive,
            }),
          }}
        >
          {!collapsed && active && (
            <div
              className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full"
              style={{ backgroundColor: sectionColor }}
            />
          )}
          <item.icon className={cn(
            iconSize,
            "shrink-0 transition-colors duration-150",
            active ? "text-white" : "text-white/45 opacity-70 group-hover:text-white/85"
          )}
            style={active ? { color: sectionColor } : undefined}
          />
          {!collapsed && <span className={cn(fontSize, "truncate transition-colors duration-150", active ? "font-medium text-white" : "text-white/84")}>{item.title}</span>}
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
    );
  };

  const SectionSeparator = () =>
    collapsed ? (
      <div className="min-h-[20px] h-5 w-full" aria-hidden="true" />
    ) : (
      <div className={cn("h-px w-auto mx-2 bg-[#2A2750]", groupGap)} />
    );

  return (
    <Sidebar className={cn("border-r border-sidebar-border bg-sidebar transition-all duration-200", collapsed ? "w-16" : "w-64")} collapsible="icon">
      <SidebarHeader className={cn("transition-all duration-200 shrink-0 z-10 sticky top-0 bg-sidebar overflow-hidden", collapsed ? "py-3 px-0 items-center justify-center gap-3" : "p-4 gap-3")}>
        <div className={cn("flex items-center", collapsed ? "justify-center" : "px-2")}>
          <BrandLogo
            size={collapsed ? "sm" : "md"}
            iconOnly={collapsed}
            inverted
            textClassName="text-white"
          />
        </div>
        <WorkspaceSwitcher />
      </SidebarHeader>

      <SidebarContent className={cn(
        "transition-all duration-200 min-h-0 flex-1 overflow-y-auto overscroll-contain group-data-[collapsible=icon]:overflow-y-auto",
        sidebarScrollbarClass,
        collapsed ? "px-0 py-1" : "px-3",
        density === 'compact' ? 'py-1' : 'py-2'
      )}>
        {isClientWorkspace ? (
          <SidebarGroup>
            {!collapsed && renderSectionLabel("Portal do Cliente", "cliente")}
            <SidebarGroupContent>
              <SidebarMenu className={cn(collapsed ? collapsedItemsGapClass : expandedItemsGapClass, collapsed && "items-center")} style={collapsed ? collapsedMenuStyle : undefined}>
                {clientNavItems.map(item => renderNavItem(item, "cliente"))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : (
          <>
            <SidebarGroup>
              {!collapsed && renderSectionLabel("Principal", "principal")}
              <SidebarGroupContent>
                <SidebarMenu className={cn(collapsed ? collapsedItemsGapClass : expandedItemsGapClass, collapsed && "items-center")} style={collapsed ? collapsedMenuStyle : undefined}>
                  {mainNavItems.map(item => renderNavItem(item, "principal"))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SectionSeparator />

            <SidebarGroup>
              {!collapsed && renderSectionLabel("Conteúdo", "conteudo")}
              <SidebarGroupContent>
                <SidebarMenu className={cn(collapsed ? collapsedItemsGapClass : expandedItemsGapClass, collapsed && "items-center")} style={collapsed ? collapsedMenuStyle : undefined}>
                  {contentNavItems.map(item => renderNavItem(item, "conteudo"))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SectionSeparator />

            <SidebarGroup>
              {!collapsed && renderSectionLabel("Marketing", "marketing")}
              <SidebarGroupContent>
                <SidebarMenu className={cn(collapsed ? collapsedItemsGapClass : expandedItemsGapClass, collapsed && "items-center")} style={collapsed ? collapsedMenuStyle : undefined}>
                  {marketingNavItems.map(item => renderNavItem(item, "marketing"))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            <SectionSeparator />

            {visibleManagementNavItems.length > 0 && (
              <>
                <SidebarGroup>
                  {!collapsed && renderSectionLabel("Gestão", "gestao")}
                  <SidebarGroupContent>
                    <SidebarMenu className={cn(collapsed ? collapsedItemsGapClass : expandedItemsGapClass, collapsed && "items-center")} style={collapsed ? collapsedMenuStyle : undefined}>
                      {visibleManagementNavItems.map(item => renderNavItem(item, "gestao"))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>

                <SectionSeparator />
              </>
            )}

            <SidebarGroup>
              {!collapsed && renderSectionLabel("Ferramentas", "ferramentas")}
              <SidebarGroupContent>
                <SidebarMenu className={cn(collapsed ? collapsedItemsGapClass : expandedItemsGapClass, collapsed && "items-center")} style={collapsed ? collapsedMenuStyle : undefined}>
                  {toolsNavItems.map(item => renderNavItem(item, "ferramentas"))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>

            {visibleSystemNavItems.length > 0 && (
              <>
                <SectionSeparator />
                <SidebarGroup>
                  {!collapsed && renderSectionLabel("Sistema", "sistema")}
                  <SidebarGroupContent>
                    <SidebarMenu className={cn(collapsed ? collapsedItemsGapClass : expandedItemsGapClass, collapsed && "items-center")} style={collapsed ? collapsedMenuStyle : undefined}>
                      {visibleSystemNavItems.map(item => renderNavItem(item, "sistema"))}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </SidebarGroup>
              </>
            )}
          </>
        )}
      </SidebarContent>

      <SidebarFooter className={cn("transition-all duration-200 shrink-0 z-10 sticky bottom-0 bg-sidebar border-t border-sidebar-border/60", collapsed ? "py-3 px-0" : "p-4")}>
        {!collapsed && !isClientWorkspace && (
          <div className="mb-3">
            <PlanUsageNavItem />
          </div>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div className={cn(
              "flex items-center gap-3 rounded-xl bg-sidebar-accent/30 border border-sidebar-border/50 hover:bg-sidebar-accent/50 transition-colors cursor-pointer group",
              collapsed ? "py-0 px-0 justify-center w-10 h-10 mx-auto rounded-full border-none bg-transparent hover:bg-sidebar-accent/30" : "p-3"
            )}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center bg-[hsl(var(--brand-primary))] shadow-sm group-hover:shadow-md transition-shadow shrink-0">
                <span className="text-white text-xs font-bold">
                  {userInitials}
                </span>
              </div>
              {!collapsed && (
                <>
                  <div className="flex flex-col overflow-hidden flex-1">
                    <span className="text-sm font-semibold text-sidebar-foreground truncate">
                      {displayName}
                    </span>
                    <span className="text-xs text-sidebar-muted-foreground truncate">
                      {displayEmail}
                    </span>
                  </div>
                  <Settings className="h-4 w-4 text-sidebar-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </>
              )}
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align={collapsed ? "center" : "end"} side={collapsed ? "right" : "right"} className="w-56 ml-2">
            <DropdownMenuLabel>Minha Conta</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {!isClientWorkspace && (
              <>
                <DropdownMenuItem onClick={() => navigate("/configuracoes?tab=profile")}>
                  <User className="mr-2 h-4 w-4" />
                  Perfil
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/configuracoes")}>
                  <Settings className="mr-2 h-4 w-4" />
                  Configurações
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={handleSignOut}>
              <LogOut className="mr-2 h-4 w-4" />
              Sair
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
